import React from 'react';

interface PaginationProps {
  currentPage: number; // 0-based index (0 = first page)
  totalPages: number;
  onPageChange: (page: number) => void;
}

/**
 * Reusable pagination component with improved styling
 * 
 * @param currentPage - Current active page (0-based)
 * @param totalPages - Total number of pages available
 * @param onPageChange - Function to call when page changes
 */
const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
}) => {
  // Ensure we have valid values - always show at least one page
  const actualTotalPages = Math.max(1, totalPages);
  const safeCurrentPage = Math.min(currentPage, actualTotalPages - 1);
  
  // Calculate page numbers to display
  const getPageNumbers = () => {
    const pageNumbers: (number | string)[] = [];
    
    // Always show first page
    pageNumbers.push(0);
    
    // Calculate range around current page
    let startPage = Math.max(1, safeCurrentPage - 1);
    let endPage = Math.min(actualTotalPages - 2, safeCurrentPage + 1);
    
    // Add ellipsis after first page if needed
    if (startPage > 1) {
      pageNumbers.push('...');
    }
    
    // Add range of pages
    for (let i = startPage; i <= endPage; i++) {
      if (i > 0) { // Ensure we don't duplicate the first page
        pageNumbers.push(i);
      }
    }
    
    // Add ellipsis before last page if needed
    if (endPage < actualTotalPages - 2 && actualTotalPages > 2) {
      pageNumbers.push('...');
    }
    
    // Add last page if there is more than one page
    if (actualTotalPages > 1) {
      pageNumbers.push(actualTotalPages - 1);
    }
    
    return pageNumbers;
  };

  const pageNumbers = getPageNumbers();

  // Handle page change
  const handlePageClick = (page: number) => {
    if (page !== safeCurrentPage && page >= 0 && page < actualTotalPages) {
      onPageChange(page);
    }
  };

  // Always render the pagination component, even for a single page
  return (
    <div className="pagination">
      {/* Page information */}
      <div className="pagination-info">
        Page {safeCurrentPage + 1} of {actualTotalPages}
      </div>
      
      <div className="pagination-buttons">
        {/* Previous button */}
        <button 
          onClick={() => handlePageClick(safeCurrentPage - 1)}
          disabled={safeCurrentPage === 0}
          className="pagination-button"
          aria-label="Page précédente"
        >
          <span aria-hidden="true">&laquo;</span> Previous
        </button>
        
        {/* Page numbers */}
        {pageNumbers.map((page, index) => (
          typeof page === 'number' ? (
            <button
              key={index}
              onClick={() => handlePageClick(page)}
              className={`pagination-button ${safeCurrentPage === page ? 'active' : ''}`}
              aria-label={`Page ${page + 1}`}
              aria-current={safeCurrentPage === page ? 'page' : undefined}
            >
              {page + 1}
            </button>
          ) : (
            <span key={index} className="pagination-ellipsis">
              {page}
            </span>
          )
        ))}
        
        {/* Next button */}
        <button 
          onClick={() => handlePageClick(safeCurrentPage + 1)}
          disabled={safeCurrentPage === actualTotalPages - 1}
          className="pagination-button"
          aria-label="Page suivante"
        >
          Next <span aria-hidden="true">&raquo;</span>
        </button>
      </div>
    </div>
  );
};

export default Pagination;